import {
  Button,
  Card,
  HSpacer,
  IconButton,
  Text,
  TopAppBar,
  VSpacer,
} from '@/components/DesignSystem';
import { ReservedPointsCard } from '@/components/shared/Booking/ReservedPointsCard';
import { TotalPointsAvailableCard } from "@/components/shared/Subwallet/TotalPointsAvailableCard";
import {
  LockedSubwalletCards,
} from '@/components/shared/SubwalletBalance/LockedSubwalletCards';
import { SubwalletList } from '@/components/shared/SubwalletBalance/SubwalletList';
import { QueryKeys } from '@/constants/QueryKeys';
import { Routes } from '@/constants/Routes';
import { useDashboard } from '@/hooks/useDashboard';
import { useDashboardProgram } from '@/pages/CustomerDashboard/Energy/useDashboardProgram';
import { DashboardModal } from '@/pages/CustomerDashboard/DashboardModal';
import { Salesperson } from '@/pages/CustomerDashboard/interfaces';
import { AboutAccountRolesBanner } from '@/pages/CustomerDashboard/LinkedAccounts';
import { PointsExpirationBanner } from "@/pages/CustomerDashboard/PointsExpirationBanner";
import { DashboardApi } from '@/utilities/api/DashboardApi';
import { OrderTransactionsApi } from "@/utilities/api/OrderTransactionsApi";
import ChatBubbleOutline from '@mui/icons-material/ChatBubbleOutline';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { CircularProgress, Container, darken, Divider, Stack, useTheme } from '@mui/material';
import { CIBPageName, OrderTransactionStatus } from '@shared/enums';
import { calculateLockedPoints, formatPhoneNumber } from '@shared/utilities';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useDashboardNavigate } from './helpers';

export const WalletPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('accessToken') ?? params.accessToken;
  const navigateToPage = useDashboardNavigate();
  const location = useLocation();
  const openReservedPoints =
    !!(location.state as { openReservedPoints?: boolean } | null)?.openReservedPoints;
  const [showSellerModal, setShowSellerModal] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const { data: dashboardData } = useDashboard(accessToken);
  const { excludeEnergyBalances } = useDashboardProgram(accessToken);

  const { data: salespersons, isFetching: isSalespersonsFetching } = useQuery<Salesperson[]>(
    [QueryKeys.GET_DASHBOARD_SALESPERSONS, accessToken],
    async () => await DashboardApi.listRelevantSalespersons(accessToken!) as Salesperson[],
    {
      enabled: !!accessToken,
    },
  );

  const { data: latestProcessedOrderDate } = useQuery(
    [QueryKeys.GET_LATEST_PROCESSED_ORDER_DATE, dashboardData?.retailer.id],
    () => OrderTransactionsApi.getLatestProcessedOrderDate({
      accessToken,
      retailerId: dashboardData?.retailer.id,
    }),
    { enabled: !!dashboardData?.retailer.id },
  );

  const { data: bookingsData } = useQuery(
    [QueryKeys.LIST_BOOKINGS, accessToken],
    () => DashboardApi.listBookings(accessToken!, {
      statuses: [OrderTransactionStatus.ACTIVE],
      limit: 1000,
      page: 0,
    }),
    { enabled: !!accessToken },
  );

  if (!dashboardData) {
    return (
      <Container>
        <Stack alignItems='center'>
          <VSpacer size='14' />
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  const {
    farmer,
    linkedAccounts,
    retailer,
  } = dashboardData;
  const balances = excludeEnergyBalances(dashboardData.balances);

  const brandColor = retailer.brandColor ?? theme.palette.primary.main;

  const Header = (
    <TopAppBar
      isStatic
      leftAccessory={(
        <IconButton
          onClick={() => navigateToPage(Routes.CUSTOMER_DASHBOARD, accessToken)}
          sx={{ color: 'white' }}
          testID="back-button"
        >
          <ArrowBack />
        </IconButton>
      )}
      rightAccessory={<HSpacer size="7" />}
      sx={{ bgcolor: brandColor }}
      testID="top-app-bar"
    >
      <Text category="title-large" sx={{ color: 'white' }}>
        {t($ => $.customerDashboard.wallet.title)}
      </Text>
    </TopAppBar>
  );

  return (
    <>
      <Stack
        bgcolor={theme.palette.background.default}
        flexGrow={1}
        height="auto"
        maxWidth="599px"
        minHeight="100vh"
        minWidth="360px"
        ref={containerRef}
      >
        {Header}
        <PointsExpirationBanner accessToken={accessToken} customer={farmer}/>
        <Stack bgcolor={darken(brandColor, 0.2)} p="16px 16px 20px">
          <Text fontSize="30px" fontWeight={500} lineHeight="36px" sx={{ color: 'white' }}>
            {t($ => $.customerDashboard.wallet.hero.placeOrder)}
            <br />
            {t($ => $.customerDashboard.wallet.hero.redeemRewards)}
          </Text>
          <VSpacer size="5" />
          <Stack direction="row" gap="16px">
            {!retailer.isProgramCreationDisabled && (
              <Card
                cardContentStyle={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  justifyContent: 'space-between',
                  textAlign: 'center',
                }}
                sx={{ flex: 1 }}
                testID="message-seller-card"
              >
                <Text category="body-medium" marginBottom="12px" textAlign="left">
                  {t($ => $.customerDashboard.wallet.messageSeller)}
                </Text>
                <Button
                  color="inherit"
                  disabled={isSalespersonsFetching || !salespersons?.length}
                  onClick={() => setShowSellerModal(true)}
                  sx={{
                    alignSelf: 'center',
                    bgcolor: theme.palette.common.white,
                    borderColor: theme.palette.grey[300],
                    color: theme.palette.text.primary,
                    minWidth: 0,
                    px: '12px',
                    width: 'fit-content',
                  }}
                  testID="message-seller-button"
                  variant="outlined"
                >
                  {t($ => $.customerDashboard.wallet.message)}
                </Button>
              </Card>
            )}
            <Card
              cardContentStyle={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                justifyContent: 'space-between',
                textAlign: 'center',
              }}
              sx={{ flex: 1 }}
              testID="visit-retailer-website-card"
            >
              <Text category="body-medium" marginBottom="12px" textAlign="left">
                {t($ => $.customerDashboard.wallet.visitOnline, { retailer: retailer.name })}
              </Text>
              <Button
                color="inherit"
                disabled={!retailer.websiteUrl}
                endIcon={<ArrowForward />}
                onClick={() => window.open(retailer.websiteUrl!, '_blank')}
                sx={{
                  alignSelf: 'center',
                  bgcolor: theme.palette.common.white,
                  borderColor: theme.palette.grey[300],
                  color: theme.palette.text.primary,
                  minWidth: 0,
                  px: '12px',
                  width: 'fit-content',
                }}
                testID="visit-retailer-website-button"
                variant="outlined"
              >
                {t($ => $.customerDashboard.wallet.go)}
              </Button>
            </Card>
          </Stack>
        </Stack>
        <VSpacer size="4" />
        {!!latestProcessedOrderDate && (
          <>
            <Stack direction="row" justifyContent="center">
              <Text category="body-small" sx={{ color: theme.palette.common.black }}>
                {t($ => $.common.updated, { date: latestProcessedOrderDate })}
              </Text>
            </Stack>
            <VSpacer size="4" />
          </>
        )}
        {!!farmer?.accountGroupRole && !!linkedAccounts?.length && (
          <Stack px="16px">
            <AboutAccountRolesBanner
              brandColor={brandColor}
              customerRole={farmer.accountGroupRole}
              linkedAccounts={linkedAccounts}
              pageName={CIBPageName.CRD_Wallet}
              showMobileVersion={true}
            />
          </Stack>
        )}
        <Stack flexGrow={1}>
          <VSpacer size="5" />
          <Stack px="16px">
            <TotalPointsAvailableCard
              accountGroupRole={farmer?.accountGroupRole}
              balances={balances}
              boundaryRef={containerRef}
              brandColor={brandColor}
              retailer={retailer}
            />
            <VSpacer size="5" />
          </Stack>
          <ReservedPointsCard
            balances={balances}
            bookings={bookingsData?.data ?? []}
            brandColor={brandColor}
            customer={farmer}
            forceMobileView
            initialOpenModal={openReservedPoints}
            linkedAccounts={linkedAccounts}
            sx={{ px: '16px', pb: '16px' }}
          />
          <Text category="title-medium" px="16px">
            {t(($) => $.customerDashboard.wallet.availablePoints)}
          </Text>
          <VSpacer size="4" />
          <Stack px="16px">
            <SubwalletList
              accessToken={accessToken}
              brandColor={brandColor}
              customer={farmer}
              hideLoyaltySkus
              subwalletBalances={balances}
            />
            <VSpacer size="5" />
          </Stack>
          {!!calculateLockedPoints(balances) && (
            <>
              <Text category="title-medium" px="16px">
                {t(($) => $.wallet.locked.title)}
              </Text>
              <VSpacer size="4" />
              <LockedSubwalletCards
                accessToken={accessToken}
                brandColor={brandColor}
                customer={farmer}
                subwalletBalances={balances}
                sx={{ px: '16px', pb: '16px' }}
              />
            </>
          )}
        </Stack>
      </Stack>
      {showSellerModal && !!salespersons && (
        <SellerModal
          onClose={() => setShowSellerModal(false)}
          salespersons={salespersons}
        />
      )}
    </>
  );
};

interface SellerModalProps {
  onClose: () => void,
  salespersons: Salesperson[],
}

export const SellerModal = ({
  onClose,
  salespersons,
}: SellerModalProps) => {
  const { t } = useTranslation();
  return (
    <DashboardModal
      hideCloseButtonIcon
      onClose={onClose}
      open
      testID="seller-modal"
      title={t($ => $.customerDashboard.wallet.selectSeller)}
    >
    {salespersons.map((salesperson) => (
      <SellerCard
        key={salesperson.id}
        salesperson={salesperson}
      />
    ))}
    <Divider />
  </DashboardModal>
  );
};

interface SellerCardProps {
  salesperson: Salesperson,
}

const SellerCard = ({
  salesperson,
}: SellerCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <Stack alignItems="center" direction="row" justifyContent="space-between" p="16px">
      <Stack>
        {!!salesperson.userLocation && (
          <>
            <Text category="body-small" sx={{ color: theme.palette.grey[600] }}>
              {salesperson.userLocation.name}
            </Text>
            <VSpacer size="1" />
          </>
        )}
        <Text category="title-medium">
          {salesperson.businessName}
        </Text>
        {!!salesperson.telephone && (
          <Text category="body-medium" sx={{ color: theme.palette.grey[700] }}>
            {formatPhoneNumber(salesperson.telephone, 'paren')}
          </Text>
        )}
      </Stack>
      <HSpacer size="5" />
      {!!salesperson.telephone && (
        <Button
          color="inherit"
          disabled={!salesperson.telephone}
          href={`sms:${salesperson.telephone}`}
          startIcon={<ChatBubbleOutline />}
          sx={{ borderColor: theme.palette.grey[300] }}
          testID={`message-seller-button-${salesperson.id}`}
          variant="outlined"
        >
          {t($ => $.customerDashboard.wallet.message)}
        </Button>
      )}
    </Stack>
  );
};
